/**
 * «открыть ▾» полосы выбора «Планов»: карточка товара и отмеченные планы в новом
 * окне. Вид — как у смарт-кнопок CRM (`smartStyle`), пункты — настоящие ссылки
 * `target=_blank`: Ctrl-клик и средняя кнопка работают сами. Панель раскрывается
 * вниз, поверх таблицы, как меню CRM этой же полосы.
 */

import { ChevronDown } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { КНОПКА_УЗКАЯ, место, type Место, СТРОКА, ШИРИНА } from "@/components/smartStyle";
import { usePopover } from "@/lib/usePopover";

function Пункт({ href, why, children }: { href: string | null; why?: string | undefined; children: string }) {
  if (href === null) {
    return (
      <span
        role="menuitem"
        aria-disabled="true"
        title={why}
        className={`${СТРОКА} cursor-not-allowed text-fg-faint hover:bg-transparent`}
      >
        <span className="truncate">{children}</span>
      </span>
    );
  }
  return (
    <a role="menuitem" href={href} target="_blank" rel="noopener" className={СТРОКА}>
      <span className="truncate">{children}</span>
    </a>
  );
}

export function OpenMenu({
  cardHref,
  cardWhy,
  plansHref,
  plansWhy,
}: {
  /** Карточка первого товара; `null` — список не помещается в адрес. */
  cardHref: string | null;
  cardWhy?: string | undefined;
  /** Отмеченные планы; `null` — их больше потолка запроса. */
  plansHref: string | null;
  plansWhy?: string | undefined;
}) {
  const [open, setOpen] = useState(false);
  const [размещение, setРазмещение] = useState<Место>({ maxHeight: 400, right: false });
  const box = useRef<HTMLDivElement>(null);
  const кнопка = useRef<HTMLButtonElement>(null);

  usePopover(open, () => setOpen(false), box);

  useLayoutEffect(() => {
    if (open && кнопка.current) setРазмещение(место(кнопка.current, true));
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const мерить = () => {
      if (кнопка.current) setРазмещение(место(кнопка.current, true));
    };
    window.addEventListener("resize", мерить);
    return () => window.removeEventListener("resize", мерить);
  }, [open]);

  return (
    <div ref={box} className="relative flex-none" data-annot="открыть отмеченные">
      <button
        ref={кнопка}
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((on) => !on)}
        title="открыть карточку товара или отмеченные планы в новой вкладке"
        className={`${КНОПКА_УЗКАЯ} hover:bg-bg-hover`}
      >
        открыть
        <ChevronDown className="h-3 w-3 text-fg-faint" />
      </button>
      {open ? (
        <div
          role="menu"
          aria-label="открыть"
          style={{ width: ШИРИНА, maxHeight: размещение.maxHeight }}
          onClick={() => setOpen(false)}
          className={
            "absolute top-full z-50 mt-1 overflow-y-auto overscroll-contain rounded-[var(--radius-ui)] border border-border bg-bg-elevated py-1 shadow-lg " +
            (размещение.right ? "right-0" : "left-0")
          }
        >
          <Пункт href={cardHref} why={cardWhy}>
            Карточка товара
          </Пункт>
          <Пункт href={plansHref} why={plansWhy}>
            Отмеченные планы в новом окне
          </Пункт>
        </div>
      ) : null}
    </div>
  );
}
